import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Trash2, Target, Calculator, RefreshCw, Check, Pencil, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

import { API } from '../utils/api';

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Custom tooltip with better contrast
const CustomTooltip = ({ active, payload, label, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 shadow-lg">
        <p className="text-white font-medium mb-1">{label}</p>
        <p className="text-green-400 font-bold">
          {formatCurrency(payload[0].value, currency)}
        </p>
      </div>
    );
  }
  return null;
};

export default function FuturePlanning({ user }) {
  const [plans, setPlans] = useState([]);
  const [onTrackMap, setOnTrackMap] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    monthly_savings: "",
    annual_savings: "",
    monthly_savings_increase_percent: "3",
    annual_savings_increase_percent: "3",
    weighted_annual_return: "6",
    start_balance: "0",
    start_year: new Date().getFullYear().toString(),
    years_to_forecast: "10",
    one_time_events: [],
    target_amount: "",
  });
  const [newEvent, setNewEvent] = useState({ year: "", description: "", amount: "", event_type: "expense" });
  const [etaPreview, setEtaPreview] = useState(null);
  const [etaLoading, setEtaLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live ETA preview while typing target/monthly/annual/return/balance
  useEffect(() => {
    const target = parseFloat(formData.target_amount);
    if (!target || target <= 0 || !dialogOpen) {
      setEtaPreview(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setEtaLoading(true);
        const payload = {
          name: formData.name || "preview",
          selected_account_ids: selectedAccountIds,
          monthly_savings: parseFloat(formData.monthly_savings) || 0,
          annual_savings: parseFloat(formData.annual_savings) || 0,
          monthly_savings_increase_percent:
            parseFloat(formData.monthly_savings_increase_percent) || 0,
          annual_savings_increase_percent:
            parseFloat(formData.annual_savings_increase_percent) || 0,
          weighted_annual_return: parseFloat(formData.weighted_annual_return) || 0,
          start_balance: parseFloat(formData.start_balance) || 0,
          start_year: parseInt(formData.start_year) || new Date().getFullYear(),
          years_to_forecast: Math.max(1, parseInt(formData.years_to_forecast) || 10),
          one_time_events: formData.one_time_events || [],
          target_amount: target,
        };
        const res = await axios.post(`${API}/future-plans/eta`, payload);
        setEtaPreview(res.data);
      } catch (err) {
        setEtaPreview(null);
      } finally {
        setEtaLoading(false);
      }
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.target_amount,
    formData.monthly_savings,
    formData.annual_savings,
    formData.monthly_savings_increase_percent,
    formData.annual_savings_increase_percent,
    formData.weighted_annual_return,
    formData.start_balance,
    formData.start_year,
    formData.years_to_forecast,
    dialogOpen,
  ]);

  const fetchData = async () => {
    try {
      const [plansRes, accountsRes] = await Promise.all([
        axios.get(`${API}/future-plans`),
        axios.get(`${API}/accounts`)
      ]);
      setPlans(plansRes.data);
      setAccounts(accountsRes.data);
      if (plansRes.data.length > 0) {
        setSelectedPlan(plansRes.data[0]);
      }
      // Fetch on-track summary (don't block main render)
      try {
        const summary = await axios.get(`${API}/future-plans/on-track-summary`);
        const map = {};
        (summary.data?.summary || []).forEach((s) => {
          map[s.plan_id] = s;
        });
        setOnTrackMap(map);
      } catch (e) {
        console.error("on-track summary failed:", e);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const calculateDefaults = async () => {
    if (selectedAccountIds.length === 0) {
      toast.error("Please select at least one account");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/future-plans/calculate-defaults`, {
        account_ids: selectedAccountIds
      });
      setFormData(prev => ({
        ...prev,
        start_balance: response.data.start_balance.toString(),
        monthly_savings: response.data.monthly_savings.toString(),
        annual_savings: response.data.annual_savings.toString(),
        weighted_annual_return: response.data.weighted_annual_return.toString(),
        start_year: response.data.start_year.toString(),
      }));
      toast.success("Values calculated from selected accounts");
    } catch (error) {
      console.error("Error calculating defaults:", error);
      toast.error("Failed to calculate defaults");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedAccountIds.length === 0) {
      toast.error("Please select at least one account");
      return;
    }
    
    try {
      const planData = {
        ...formData,
        selected_account_ids: selectedAccountIds,
        monthly_savings: parseFloat(formData.monthly_savings) || 0,
        annual_savings: parseFloat(formData.annual_savings) || 0,
        monthly_savings_increase_percent: parseFloat(formData.monthly_savings_increase_percent) || 0,
        annual_savings_increase_percent: parseFloat(formData.annual_savings_increase_percent) || 0,
        weighted_annual_return: parseFloat(formData.weighted_annual_return) || 0,
        start_balance: parseFloat(formData.start_balance) || 0,
        start_year: parseInt(formData.start_year),
        years_to_forecast: parseInt(formData.years_to_forecast),
        target_amount:
          formData.target_amount === "" || formData.target_amount === null
            ? null
            : parseFloat(formData.target_amount) || null,
      };

      if (editingPlanId) {
        // Update existing plan
        await axios.put(`${API}/future-plans/${editingPlanId}`, planData);
        toast.success("Plan updated successfully");
      } else {
        // Create new plan
        await axios.post(`${API}/future-plans`, planData);
        toast.success("Plan created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error(editingPlanId ? "Failed to update plan" : "Failed to create plan");
    }
  };

  const handleDelete = async (planId) => {
    try {
      await axios.delete(`${API}/future-plans/${planId}`);
      toast.success("Plan deleted");
      if (selectedPlan?.plan_id === planId) {
        setSelectedPlan(null);
      }
      fetchData();
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("Failed to delete plan");
    }
  };

  const toggleAccountSelection = (accountId) => {
    setSelectedAccountIds(prev => 
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const selectAllInCategory = (category) => {
    const categoryAccountIds = accounts
      .filter(acc => acc.category === category)
      .map(acc => acc.account_id);
    
    const allSelected = categoryAccountIds.every(id => selectedAccountIds.includes(id));
    
    if (allSelected) {
      setSelectedAccountIds(prev => prev.filter(id => !categoryAccountIds.includes(id)));
    } else {
      setSelectedAccountIds(prev => [...new Set([...prev, ...categoryAccountIds])]);
    }
  };

  const addOneTimeEvent = () => {
    if (!newEvent.year || !newEvent.description || !newEvent.amount) {
      toast.error("Please fill all event fields");
      return;
    }
    setFormData({
      ...formData,
      one_time_events: [
        ...formData.one_time_events,
        {
          year: parseInt(newEvent.year),
          description: newEvent.description,
          amount: parseFloat(newEvent.amount),
          event_type: newEvent.event_type,
        },
      ],
    });
    setNewEvent({ year: "", description: "", amount: "", event_type: "expense" });
  };

  const removeOneTimeEvent = (index) => {
    setFormData({
      ...formData,
      one_time_events: formData.one_time_events.filter((_, i) => i !== index),
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      monthly_savings: "",
      annual_savings: "",
      monthly_savings_increase_percent: "3",
      annual_savings_increase_percent: "3",
      weighted_annual_return: "6",
      start_balance: "0",
      start_year: new Date().getFullYear().toString(),
      years_to_forecast: "10",
      one_time_events: [],
      target_amount: "",
    });
    setNewEvent({ year: "", description: "", amount: "", event_type: "expense" });
    setSelectedAccountIds([]);
    setEditingPlanId(null);
    setEtaPreview(null);
  };

  const openCreateDialog = () => {
    resetForm();
    // Pre-select all accounts
    setSelectedAccountIds(accounts.map(acc => acc.account_id));
    setDialogOpen(true);
  };

  const openEditDialog = async (plan) => {
    setEditingPlanId(plan.plan_id);
    const accountIds = plan.selected_account_ids || accounts.map(acc => acc.account_id);
    setSelectedAccountIds(accountIds);

    // Refresh start_balance from the LATEST snapshot sum of selected accounts
    // (excluding soft-deleted) so the edit dialog reflects current reality
    // instead of the balance frozen at plan creation.
    let refreshedStartBalance = plan.start_balance;
    try {
      const resp = await axios.post(`${API}/future-plans/calculate-defaults`, {
        account_ids: accountIds,
      });
      if (resp?.data?.start_balance !== undefined && resp.data.start_balance !== null) {
        refreshedStartBalance = resp.data.start_balance;
      }
    } catch (err) {
      console.warn("Could not refresh start_balance on edit; using stored value", err);
    }

    setFormData({
      name: plan.name,
      monthly_savings: plan.monthly_savings.toString(),
      annual_savings: plan.annual_savings.toString(),
      monthly_savings_increase_percent: plan.monthly_savings_increase_percent.toString(),
      annual_savings_increase_percent: plan.annual_savings_increase_percent.toString(),
      weighted_annual_return: plan.weighted_annual_return.toString(),
      start_balance: refreshedStartBalance.toString(),
      start_year: plan.start_year.toString(),
      years_to_forecast: plan.years_to_forecast.toString(),
      one_time_events: plan.one_time_events || [],
      target_amount:
        plan.target_amount !== null && plan.target_amount !== undefined
          ? plan.target_amount.toString()
          : "",
    });
    setDialogOpen(true);
  };

  const currency = user?.default_currency || "EUR";

  // Group accounts by category
  const accountsByCategory = accounts.reduce((acc, account) => {
    const category = account.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(account);
    return acc;
  }, {});

  // Get account names for selected plan
  const getSelectedAccountNames = (plan) => {
    if (!plan.selected_account_ids || plan.selected_account_ids.length === 0) return "All accounts";
    const selectedAccounts = accounts.filter(acc => plan.selected_account_ids.includes(acc.account_id));
    if (selectedAccounts.length <= 2) {
      return selectedAccounts.map(acc => acc.name).join(", ");
    }
    return `${selectedAccounts.length} accounts`;
  };

  if (loading) {
    return (
      <Layout user={user} pageTitle="Future Planning">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} pageTitle="Future Planning">
      <div data-testid="future-planning-page" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-gray-400">
              Create financial forecasts based on your savings goals
            </p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6 font-semibold"
            data-testid="create-plan-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Plan
          </Button>
        </div>

        {plans.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No Plans Yet</h3>
              <p className="text-gray-400 mb-6">
                Create your first financial forecast to visualize your future wealth.
              </p>
              <Button
                onClick={openCreateDialog}
                className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Plan
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Plans List */}
            <div className="lg:col-span-1 space-y-4">
              {plans.map((plan) => (
                <motion.div
                  key={plan.plan_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <Card
                    className={`cursor-pointer transition-all ${
                      selectedPlan?.plan_id === plan.plan_id
                        ? "border-green-500 bg-green-500/10"
                        : "bg-card border-border hover:border-green-500/30"
                    }`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-white">{plan.name}</h3>
                          <p className="text-sm text-gray-400">
                            {plan.start_year} - {plan.start_year + plan.years_to_forecast - 1}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {getSelectedAccountNames(plan)}
                          </p>
                          <p className="text-lg font-bold text-green-400 number-display mt-2">
                            {formatCurrency(
                              plan.forecast[plan.forecast.length - 1]?.ending_balance || 0,
                              currency
                            )}
                          </p>
                          {onTrackMap[plan.plan_id] && (
                            <div
                              className={`inline-flex items-center gap-1 text-xs font-semibold mt-2 px-2 py-0.5 rounded-full ${
                                onTrackMap[plan.plan_id].on_track
                                  ? "bg-green-500/15 text-green-300 border border-green-500/30"
                                  : "bg-red-500/15 text-red-300 border border-red-500/30"
                              }`}
                              data-testid={`on-track-badge-${plan.plan_id}`}
                            >
                              {onTrackMap[plan.plan_id].on_track ? (
                                <CheckCircle2 className="w-3 h-3" />
                              ) : (
                                <AlertTriangle className="w-3 h-3" />
                              )}
                              {onTrackMap[plan.plan_id].on_track ? "On track" : "Off track"}
                              {" · "}
                              {onTrackMap[plan.plan_id].pct_of_target}%
                            </div>
                          )}
                          {plan.target_amount && (
                            <div
                              className="mt-2 text-xs text-gray-400"
                              data-testid={`target-eta-${plan.plan_id}`}
                            >
                              <span className="text-gray-500">Goal:</span>{" "}
                              <span className="text-white font-semibold number-display">
                                {formatCurrency(plan.target_amount, currency)}
                              </span>{" "}
                              {plan.target_reached_year ? (
                                <span className="text-green-400">
                                  · reached {new Date(plan.target_reached_year, (plan.target_reached_month || 1) - 1, 1).toLocaleString("en", { month: "short", year: "numeric" })}
                                  {plan.target_reached_in_months >= 12
                                    ? ` (~${(plan.target_reached_in_months / 12).toFixed(1)} yrs)`
                                    : ` (${plan.target_reached_in_months} mo)`}
                                </span>
                              ) : (
                                <span className="text-amber-400">
                                  · not reached in forecast window
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-500 hover:text-blue-400"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(plan);
                            }}
                            data-testid={`edit-plan-${plan.plan_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-500 hover:text-red-400"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(plan.plan_id);
                            }}
                            data-testid={`delete-plan-${plan.plan_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Selected Plan Details */}
            {selectedPlan && (
              <div className="lg:col-span-3 space-y-6">
                {/* Parameters */}
                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-bold text-white">
                      Plan Parameters
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      <div className="p-3 bg-secondary rounded-xl">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Starting Balance</p>
                        <p className="text-lg font-bold text-amber-400 number-display">
                          {formatCurrency(selectedPlan.start_balance, currency)}
                        </p>
                      </div>
                      <div className="p-3 bg-secondary rounded-xl">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Monthly Savings</p>
                        <p className="text-lg font-bold text-green-400 number-display">
                          {formatCurrency(selectedPlan.monthly_savings, currency)}
                        </p>
                      </div>
                      <div className="p-3 bg-secondary rounded-xl">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Annual Savings</p>
                        <p className="text-lg font-bold text-cyan-400 number-display">
                          {formatCurrency(selectedPlan.annual_savings, currency)}
                        </p>
                      </div>
                      <div className="p-3 bg-secondary rounded-xl">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Monthly Increase</p>
                        <p className="text-lg font-bold text-purple-400 number-display">
                          {selectedPlan.monthly_savings_increase_percent}%/yr
                        </p>
                      </div>
                      <div className="p-3 bg-secondary rounded-xl">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Annual Increase</p>
                        <p className="text-lg font-bold text-pink-400 number-display">
                          {selectedPlan.annual_savings_increase_percent}%/yr
                        </p>
                      </div>
                      <div className="p-3 bg-secondary rounded-xl">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Avg. Return</p>
                        <p className="text-lg font-bold text-blue-400 number-display">
                          {selectedPlan.weighted_annual_return}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Chart */}
                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-bold text-white">
                      Wealth Forecast
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={selectedPlan.forecast}>
                          <defs>
                            <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis
                            dataKey="year"
                            stroke="#71717a"
                          />
                          <YAxis
                            tickFormatter={(value) => formatCurrency(value, currency)}
                            stroke="#71717a"
                            width={100}
                          />
                          <Tooltip content={<CustomTooltip currency={currency} />} />
                          <Area
                            type="monotone"
                            dataKey="ending_balance"
                            name="Ending Balance"
                            stroke="#22c55e"
                            strokeWidth={2}
                            fill="url(#balanceGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Forecast Table */}
                <Card className="bg-card border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-bold text-white">
                      Year-by-Year Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border">
                            <TableHead className="text-gray-400">Year</TableHead>
                            <TableHead className="text-right text-gray-400">Starting</TableHead>
                            <TableHead className="text-right text-gray-400">Monthly (×12)</TableHead>
                            <TableHead className="text-right text-gray-400">Annual</TableHead>
                            <TableHead className="text-right text-gray-400">Returns</TableHead>
                            <TableHead className="text-right text-gray-400">Events</TableHead>
                            <TableHead className="text-right text-gray-400">Ending</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedPlan.forecast.map((row) => {
                            const netEvents = (row.one_time_income || 0) - (row.one_time_expenses || 0);
                            return (
                              <TableRow key={row.year} className="border-border">
                                <TableCell className="font-medium text-white">{row.year}</TableCell>
                                <TableCell className="text-right text-gray-300 number-display">
                                  {formatCurrency(row.starting_balance, currency)}
                                </TableCell>
                                <TableCell className="text-right text-green-400 number-display">
                                  +{formatCurrency(row.monthly_contributions, currency)}
                                </TableCell>
                                <TableCell className="text-right text-cyan-400 number-display">
                                  +{formatCurrency(row.annual_contributions, currency)}
                                </TableCell>
                                <TableCell className="text-right text-blue-400 number-display">
                                  +{formatCurrency(row.interest_earned, currency)}
                                </TableCell>
                                <TableCell className={`text-right number-display ${netEvents >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {netEvents !== 0 ? (netEvents > 0 ? '+' : '') + formatCurrency(netEvents, currency) : '-'}
                                </TableCell>
                                <TableCell className="text-right font-bold text-white number-display">
                                  {formatCurrency(row.ending_balance, currency)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* One-time Events */}
                {selectedPlan.one_time_events && selectedPlan.one_time_events.length > 0 && (
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-bold text-white">
                        Planned One-Time Events
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedPlan.one_time_events.map((event, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                            <div>
                              <span className="text-white font-medium">{event.year}: </span>
                              <span className="text-gray-300">{event.description}</span>
                            </div>
                            <span className={`font-bold number-display ${event.event_type === 'income' ? 'text-green-400' : 'text-red-400'}`}>
                              {event.event_type === 'income' ? '+' : '-'}{formatCurrency(event.amount, currency)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Plan Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border text-white">
            <DialogHeader>
              <DialogTitle className="text-white">{editingPlanId ? "Edit Financial Plan" : "Create Financial Plan"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Plan Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Retirement Fund, Child A College"
                  required
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                  data-testid="plan-name-input"
                />
              </div>

              {/* Account Selection */}
              <div className="border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-gray-300">Select Accounts for Starting Balance</Label>
                  <span className="text-sm text-gray-500">{selectedAccountIds.length} selected</span>
                </div>
                
                {accounts.length === 0 ? (
                  <p className="text-gray-500 text-sm">No accounts yet. Create accounts first.</p>
                ) : (
                  <div className="space-y-4 max-h-48 overflow-y-auto">
                    {Object.entries(accountsByCategory).map(([category, categoryAccounts]) => {
                      const allSelected = categoryAccounts.every(acc => selectedAccountIds.includes(acc.account_id));
                      return (
                        <div key={category}>
                          <div 
                            className="flex items-center gap-2 mb-2 cursor-pointer hover:text-green-400"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              selectAllInCategory(category);
                            }}
                          >
                            <div 
                              className={`w-4 h-4 rounded border flex items-center justify-center ${
                                allSelected 
                                  ? 'bg-green-500 border-green-500' 
                                  : 'border-gray-500'
                              }`}
                            >
                              {allSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                              {category}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-6">
                            {categoryAccounts.map((account) => {
                              const isSelected = selectedAccountIds.includes(account.account_id);
                              return (
                                <div
                                  key={account.account_id}
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'bg-green-500/10 border border-green-500/30'
                                      : 'bg-secondary/50 hover:bg-secondary'
                                  }`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleAccountSelection(account.account_id);
                                  }}
                                >
                                  <div 
                                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                      isSelected 
                                        ? 'bg-green-500 border-green-500' 
                                        : 'border-gray-500'
                                    }`}
                                  >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-white truncate">{account.name}</p>
                                    <p className="text-xs text-gray-500">
                                      {formatCurrency(account.balance, account.currency)}
                                      {account.annual_return_percent > 0 && ` • ${account.annual_return_percent}%`}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={calculateDefaults}
                  disabled={selectedAccountIds.length === 0}
                  className="mt-3 rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Calculate from Selected
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Starting Balance ({currency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.start_balance}
                    onChange={(e) => setFormData({ ...formData, start_balance: e.target.value })}
                    placeholder="0"
                    className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                    data-testid="plan-start-balance-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Avg. Annual Return (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.weighted_annual_return}
                    onChange={(e) => setFormData({ ...formData, weighted_annual_return: e.target.value })}
                    className="mt-1 bg-secondary border-border text-white"
                    data-testid="plan-return-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Monthly Savings ({currency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.monthly_savings}
                    onChange={(e) => setFormData({ ...formData, monthly_savings: e.target.value })}
                    placeholder="1000"
                    required
                    className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                    data-testid="plan-monthly-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Monthly Increase (%/yr)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.monthly_savings_increase_percent}
                    onChange={(e) => setFormData({ ...formData, monthly_savings_increase_percent: e.target.value })}
                    className="mt-1 bg-secondary border-border text-white"
                    data-testid="plan-monthly-increase-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Annual Savings ({currency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.annual_savings}
                    onChange={(e) => setFormData({ ...formData, annual_savings: e.target.value })}
                    placeholder="5000"
                    className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                    data-testid="plan-annual-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Annual Increase (%/yr)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.annual_savings_increase_percent}
                    onChange={(e) => setFormData({ ...formData, annual_savings_increase_percent: e.target.value })}
                    className="mt-1 bg-secondary border-border text-white"
                    data-testid="plan-annual-increase-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Start Year</Label>
                  <Input
                    type="number"
                    value={formData.start_year}
                    onChange={(e) => setFormData({ ...formData, start_year: e.target.value })}
                    className="mt-1 bg-secondary border-border text-white"
                    data-testid="plan-start-year-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Years to Forecast</Label>
                  <Input
                    type="number"
                    value={formData.years_to_forecast}
                    onChange={(e) => setFormData({ ...formData, years_to_forecast: e.target.value })}
                    className="mt-1 bg-secondary border-border text-white"
                    data-testid="plan-years-input"
                  />
                </div>
              </div>

              {/* Goal target + ETA preview */}
              <div className="border-t border-border pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  <div>
                    <Label className="text-gray-300">
                      Goal target amount ({currency}) <span className="text-gray-500 text-xs">— optional</span>
                    </Label>
                    <Input
                      type="number"
                      value={formData.target_amount}
                      onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                      placeholder={`e.g. 500000`}
                      className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                      data-testid="plan-target-amount-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter a lump-sum goal and we&apos;ll estimate when your forecast reaches it.
                    </p>
                  </div>
                  <div
                    className="p-3 bg-secondary rounded-xl border border-border min-h-[92px]"
                    data-testid="plan-eta-preview"
                  >
                    {!formData.target_amount ? (
                      <p className="text-xs text-gray-500">
                        Set a goal target to see how long it will take to reach.
                      </p>
                    ) : etaLoading ? (
                      <p className="text-xs text-gray-400">Calculating...</p>
                    ) : etaPreview?.target_reached_year ? (
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider">
                          Estimated to hit
                        </p>
                        <p className="text-lg font-bold text-green-400 number-display">
                          {formatCurrency(parseFloat(formData.target_amount) || 0, currency)}
                        </p>
                        <p className="text-sm text-white mt-1">
                          by{" "}
                          <span className="font-semibold">
                            {new Date(
                              etaPreview.target_reached_year,
                              (etaPreview.target_reached_month || 1) - 1,
                              1
                            ).toLocaleString("en", { month: "long", year: "numeric" })}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {etaPreview.target_reached_in_months} months from today
                          {etaPreview.target_reached_in_months >= 12 &&
                            ` (~${(etaPreview.target_reached_in_months / 12).toFixed(1)} yrs)`}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs text-amber-400 font-semibold">
                          Not reachable within {formData.years_to_forecast || 10} years
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Try increasing savings, return %, or the forecast horizon.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* One-time Events */}
              <div className="border-t border-border pt-4">
                <Label className="text-gray-300">One-Time Events (Optional)</Label>
                <p className="text-xs text-gray-500 mb-3">
                  Add future expenses (education, home) or income (inheritance, bonus)
                </p>
                
                <div className="grid grid-cols-5 gap-2 mb-3">
                  <Input
                    type="number"
                    placeholder="Year"
                    value={newEvent.year}
                    onChange={(e) => setNewEvent({ ...newEvent, year: e.target.value })}
                    className="bg-secondary border-border text-white placeholder:text-gray-500"
                    data-testid="event-year-input"
                  />
                  <Input
                    placeholder="Description"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    className="col-span-2 bg-secondary border-border text-white placeholder:text-gray-500"
                    data-testid="event-description-input"
                  />
                  <Select value={newEvent.event_type} onValueChange={(value) => setNewEvent({ ...newEvent, event_type: value })}>
                    <SelectTrigger className="bg-secondary border-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="expense" className="text-gray-300 focus:text-white focus:bg-secondary">Expense</SelectItem>
                      <SelectItem value="income" className="text-gray-300 focus:text-white focus:bg-secondary">Income</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="button" onClick={addOneTimeEvent} variant="outline" className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <Input
                  type="number"
                  placeholder={`Amount (${currency})`}
                  value={newEvent.amount}
                  onChange={(e) => setNewEvent({ ...newEvent, amount: e.target.value })}
                  className="mb-3 bg-secondary border-border text-white placeholder:text-gray-500"
                  data-testid="event-amount-input"
                />

                {formData.one_time_events.length > 0 && (
                  <div className="space-y-2">
                    {formData.one_time_events.map((event, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-secondary rounded-lg text-sm">
                        <span className="text-gray-300">
                          {event.year}: {event.description}
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded ${event.event_type === 'income' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {event.event_type}
                          </span>
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium number-display ${event.event_type === 'income' ? 'text-green-400' : 'text-red-400'}`}>
                            {event.event_type === 'income' ? '+' : '-'}{formatCurrency(event.amount, currency)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-gray-500 hover:text-red-400"
                            onClick={() => removeOneTimeEvent(index)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold" 
                  data-testid="save-plan-btn"
                  disabled={selectedAccountIds.length === 0}
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  {editingPlanId ? "Update Plan" : "Create Plan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
